import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, Printer, Plus, Trophy, CheckCircle, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export const ProcurementSourcing = ({ data, canEdit, onChanged }) => {
  const [sourcing, setSourcing] = useState(null);
  const [editTor, setEditTor] = useState(false);
  const [torForm, setTorForm] = useState({ tor_type: "spec", title: "", content: "", delivery_location: "", closing_date: "" });
  const [showQuote, setShowQuote] = useState(false);
  const [quote, setQuote] = useState({ vendor_name: "", amount: "", lead_time: "", notes: "" });
  const [rfq, setRfq] = useState(null);

  const load = () => api.get(`/procurement/${data.id}/sourcing`)
    .then((r) => {
      setSourcing(r.data);
      if (r.data.tor) setTorForm({ tor_type: r.data.tor.tor_type, title: r.data.tor.title || "", content: r.data.tor.content || "", delivery_location: r.data.tor.delivery_location || "", closing_date: r.data.tor.closing_date || "" });
    })
    .catch(() => {});
  useEffect(() => { load(); }, [data.id]); // eslint-disable-line

  if (!sourcing || !["B", "C"].includes(data.stream)) return null;
  const tor = sourcing.tor;
  const catInfo = (sourcing.categories || []).find((c) => c.key === sourcing.category);

  const saveTor = async () => {
    try {
      await api.put(`/procurement/${data.id}/tor`, torForm);
      toast.success(tor ? "TOR / spec updated" : "TOR / spec saved");
      setEditTor(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const confirmTor = async () => {
    try {
      await api.post(`/procurement/${data.id}/tor/confirm`);
      toast.success("TOR confirmed — budget & scope signed off");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setCategory = async (v) => {
    try {
      await api.patch(`/procurement/${data.id}/category`, { procurement_category: v });
      toast.success("Category updated");
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const addQuote = async () => {
    try {
      await api.post(`/procurement/${data.id}/quotations`, { ...quote, amount: Number(quote.amount) || 0 });
      toast.success("Quotation recorded");
      setShowQuote(false); setQuote({ vendor_name: "", amount: "", lead_time: "", notes: "" }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const selectQuote = async (q) => {
    try {
      await api.post(`/procurement/${data.id}/quotations/${q.id}/select`);
      toast.success(`${q.vendor_name} selected — award goes through the existing DOA approval`);
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeQuote = async (q) => {
    try {
      await api.delete(`/procurement/${data.id}/quotations/${q.id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openRfq = async () => {
    try {
      const { data: doc } = await api.get(`/procurement/${data.id}/rfq-document`);
      setRfq(doc);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const lowest = Math.min(...(sourcing.quotations || []).map((q) => q.amount).filter(Boolean));

  return (
    <div className="dc-card overflow-hidden" data-testid="proc-sourcing-section">
      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2">
        <span className="overline flex-1">Sourcing · TOR &amp; quotations</span>
        <Select value={sourcing.category} onValueChange={setCategory} disabled={!canEdit}>
          <SelectTrigger className="h-7 w-56 text-xs" data-testid="proc-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {(sourcing.categories || []).map((c) => (
              <SelectItem key={c.key} value={c.key}>{c.label}{c.tor_required ? " · TOR required" : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3 p-4">
        <div className={`rounded-md border px-3 py-2 text-[11px] ${sourcing.tor_required && !(tor?.confirmed) ? "border-amber-200 bg-amber-50/70 text-amber-800" : "border-slate-200 bg-slate-50 text-slate-600"}`}
             data-testid="proc-tor-hint">
          {catInfo?.hint}{sourcing.tor_required && !(tor?.confirmed) && " A confirmed TOR is needed before sourcing (P4)."}
        </div>

        {/* TOR / spec */}
        {!editTor ? (
          <div className="flex items-start gap-3">
            <FileText size={16} className="mt-0.5 text-slate-400" />
            <div className="min-w-0 flex-1">
              {tor ? (
                <>
                  <div className="text-sm font-medium text-slate-900">
                    {tor.title}
                    <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 font-mono-alt text-[9px] uppercase text-slate-500">{tor.tor_type}</span>
                    {tor.confirmed ? (
                      <span className="ml-1.5 inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700" data-testid="proc-tor-confirmed-chip">
                        <CheckCircle size={10} /> confirmed by {tor.confirmed_by}
                      </span>
                    ) : (
                      <span className="ml-1.5 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">awaiting manager confirmation</span>
                    )}
                  </div>
                  {tor.content && <p className="mt-0.5 line-clamp-3 whitespace-pre-line text-xs text-slate-500">{tor.content}</p>}
                  {(tor.closing_date || tor.delivery_location) && (
                    <div className="mt-0.5 text-[11px] text-slate-500">
                      {tor.closing_date && <>Closing {formatDate(tor.closing_date)}</>}{tor.closing_date && tor.delivery_location && " · "}{tor.delivery_location && <>Deliver to {tor.delivery_location}</>}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-xs text-slate-400">No TOR / specification yet.</div>
              )}
            </div>
            <div className="flex shrink-0 gap-1.5">
              {canEdit && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditTor(true)} data-testid="proc-tor-edit-btn">
                  {tor ? "Edit" : "Add TOR / spec"}
                </Button>
              )}
              {canEdit && tor && !tor.confirmed && (
                <Button size="sm" className="h-7 bg-emerald-600 text-xs text-white hover:bg-emerald-700" onClick={confirmTor} data-testid="proc-tor-confirm-btn">
                  <CheckCircle size={11} className="mr-1" /> Confirm (manager)
                </Button>
              )}
              {tor && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openRfq} data-testid="proc-rfq-btn">
                  <Printer size={11} className="mr-1" /> RFQ
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-2 rounded-md border border-slate-200 p-3">
            <div className="grid grid-cols-2 gap-2">
              <Select value={torForm.tor_type} onValueChange={(v) => setTorForm({ ...torForm, tor_type: v })}>
                <SelectTrigger data-testid="tor-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="spec">Simple specification</SelectItem>
                  <SelectItem value="boq">Bill of quantities</SelectItem>
                  <SelectItem value="tor">Full Terms of Reference</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="Title" value={torForm.title} onChange={(e) => setTorForm({ ...torForm, title: e.target.value })} data-testid="tor-title" />
            </div>
            <Textarea rows={5} placeholder={"Objectives / scope / deliverables / evaluation criteria…\nThis text auto-populates the RFQ — no re-typing."}
                      value={torForm.content} onChange={(e) => setTorForm({ ...torForm, content: e.target.value })} data-testid="tor-content" />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Delivery location" value={torForm.delivery_location} onChange={(e) => setTorForm({ ...torForm, delivery_location: e.target.value })} data-testid="tor-location" />
              <Input type="date" value={torForm.closing_date} onChange={(e) => setTorForm({ ...torForm, closing_date: e.target.value })} data-testid="tor-closing" />
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditTor(false)}>Cancel</Button>
              <Button size="sm" onClick={saveTor} disabled={!torForm.content.trim()} data-testid="tor-save-btn">Save</Button>
            </div>
          </div>
        )}

        {/* Quotations comparison */}
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
              Quotations ({(sourcing.quotations || []).length})
            </span>
            {canEdit && (
              <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => setShowQuote(true)} data-testid="quote-add-btn">
                <Plus size={10} className="mr-1" /> Add quotation
              </Button>
            )}
          </div>
          {(sourcing.quotations || []).length === 0 ? (
            <div className="rounded border border-dashed border-slate-200 px-3 py-3 text-center text-[11px] text-slate-400">
              No quotations yet — send the auto-generated RFQ to suppliers, then record their quotes here to compare.
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-100 font-mono-alt text-[9px] uppercase tracking-widest text-slate-400">
                  <th className="py-1.5 pr-2">Vendor</th><th className="py-1.5 pr-2 text-right">Amount</th>
                  <th className="py-1.5 pr-2">Lead time</th><th className="py-1.5 pr-2">Notes</th><th className="py-1.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {(sourcing.quotations || []).map((q) => (
                  <tr key={q.id} className={q.selected ? "bg-emerald-50/60" : ""} data-testid={`quote-row-${q.vendor_name.replaceAll(" ", "-")}`}>
                    <td className="py-1.5 pr-2 font-medium text-slate-800">
                      {q.vendor_name}
                      {q.selected && <span className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-emerald-100 px-1 text-[9px] font-semibold text-emerald-700"><Trophy size={9} /> SELECTED</span>}
                    </td>
                    <td className={`py-1.5 pr-2 text-right font-mono-alt tabular-nums ${q.amount === lowest ? "font-semibold text-emerald-700" : ""}`}>
                      {formatCurrency(q.amount)}{q.amount === lowest && <span className="ml-1 text-[9px]">lowest</span>}
                    </td>
                    <td className="py-1.5 pr-2 text-slate-500">{q.lead_time || "—"}</td>
                    <td className="py-1.5 pr-2 text-slate-500 max-w-40 truncate" title={q.notes}>{q.notes || "—"}</td>
                    <td className="py-1.5 text-right">
                      {canEdit && !q.selected && (
                        <span className="inline-flex gap-1">
                          <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px]" onClick={() => selectQuote(q)} data-testid={`quote-select-${q.id}`}>
                            Select
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 px-1 text-slate-400 hover:text-red-600" onClick={() => removeQuote(q)} data-testid={`quote-delete-${q.id}`}>
                            <Trash size={11} />
                          </Button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* add quotation dialog */}
      <Dialog open={showQuote} onOpenChange={(v) => !v && setShowQuote(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record a quotation</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Vendor *</Label>
              <Input value={quote.vendor_name} onChange={(e) => setQuote({ ...quote, vendor_name: e.target.value })} data-testid="quote-vendor" />
            </div>
            <div>
              <Label>Amount (USD) *</Label>
              <Input type="number" step="0.01" value={quote.amount} onChange={(e) => setQuote({ ...quote, amount: e.target.value })} data-testid="quote-amount" />
            </div>
            <div>
              <Label>Lead time</Label>
              <Input placeholder="e.g. 2 weeks" value={quote.lead_time} onChange={(e) => setQuote({ ...quote, lead_time: e.target.value })} data-testid="quote-leadtime" />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Input value={quote.notes} onChange={(e) => setQuote({ ...quote, notes: e.target.value })} data-testid="quote-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuote(false)}>Cancel</Button>
            <Button onClick={addQuote} disabled={!quote.vendor_name.trim() || !(Number(quote.amount) > 0)} data-testid="quote-save-btn">Save quotation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RFQ printable */}
      <Dialog open={!!rfq} onOpenChange={(v) => !v && setRfq(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rfq-doc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Request for Quotation · {rfq?.rfq_no}</span>
              <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="rfq-print-btn">
                <Printer size={12} className="mr-1" /> Print / PDF
              </Button>
            </DialogTitle>
          </DialogHeader>
          {rfq && (
            <div className="space-y-3 text-sm print:text-black">
              <div className="border-b border-slate-200 pb-2">
                <div className="font-display text-lg font-bold">{rfq.title}</div>
                <div className="text-xs text-slate-500">
                  Category: {rfq.category} · {rfq.closing_date ? `Quotations close ${formatDate(rfq.closing_date)}` : "Closing date to be advised"}
                  {rfq.delivery_location && ` · Deliver to: ${rfq.delivery_location}`}
                </div>
              </div>
              {rfq.description && <p className="text-xs text-slate-600">{rfq.description}</p>}
              <div>
                <div className="overline mb-1">Specification / Terms of Reference</div>
                <pre className="whitespace-pre-wrap rounded bg-slate-50 p-3 text-xs text-slate-700">{rfq.specification || "See attached document."}</pre>
              </div>
              <div>
                <div className="overline mb-1">Terms &amp; conditions</div>
                <ul className="list-disc pl-5 text-xs text-slate-600">
                  {(rfq.terms || []).map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
              <div className="text-[11px] text-slate-400">Contact: {rfq.contact} · Generated {formatDate(rfq.issued_at)} — auto-populated from the stored TOR/spec.</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProcurementSourcing;
